<!--
 * @Description: 基于 DcTable 二次定制化的表格组件
 * @Author: mdb
 * @Date: 2022-09-07 20:08:34
 * @LastEditTime: 2023-03-06 11:12:20
 * @LastEditors: mdb
-->
<template>
  <dc-table
    class="custom-table ant-table-striped"
    size="small"
    :dynamicColumnSetting="dynamicColumnSetting"
    :pagination="pagination"
    :outsidePagination="true"
    :columns="columnsPro"
    v-bind="$attrs"
  >
    <!-- 个性化头部单元格 -- antd自身插槽 -->
    <template #headerCell="{ title, column }">
      <slot name="headerCell" :title="title" :column="column" />
    </template>

    <!-- 个性化单元格 -- antd自身插槽 -->
    <template #bodyCell="{ text, record, index, column }">
      <template v-if="column.cosType === 'link'">
        <router-link :to="linkParamsFormat(column, record)" :title="text">{{ text || '--' }}</router-link>
      </template>
      <slot name="bodyCell" :text="text" :record="record" :index="index" :column="column" />
    </template>
  </dc-table>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import queryString from 'query-string'
import { cloneDeep, camelCase } from 'lodash-es'
import { useI18n } from 'vue-i18n'
import type { DcTableType } from './index'
import type { KeyString } from '@/typings/common'
const { t } = useI18n()

const props = defineProps({
  // 表格序号
  serial: {
    type: Boolean,
    default: true
  },
  // 列描述数据对象
  columns: {
    type: Array,
    default: () => []
  },
  pagination: {
    type: [Object, Boolean],
    default: () => false
  },
  dynamicColumnSetting: {
    type: Boolean,
    default: true
  }
})

const columnsPro = computed(() => {
  const columns = cloneDeep(props.columns)
  // 处理表格序号列
  if (props.serial) {
    columns.unshift({
      title: t('common.serial'),
      dataIndex: 'index',
      width: 65,
      fixed: 'left',
      customRender: ({ index }: { index: number }) => `${index + 1}`
    })
  }
  columns.forEach((item: any, index) => {
    // 超过宽度将自动省略，默认显示 ...
    if (item.dataIndex !== 'index' && item.dataIndex !== 'action') {
      item.ellipsis = item.ellipsis ?? true
    }
    // 时间范围格式化/固定宽度
    if (item.cosType === 'timeRange') {
      item.width = item.width ?? 345
      item.customRender = ({ column, record }: { record: any; column: DcTableType.ColumnItem }) => {
        const [start, end] = column.dataIndex as string[]
        if (!record[start] && !record[end]) return '--'
        return `${record[start]} - ${record[end]}`
      }
    }
  })
  return columns
})

/**
 * 生成跳转链接函数
 * @param column 表头配置参数
 * @param rows 行数据
 */
const linkParamsFormat = (column: DcTableType.ColumnItem, rows: KeyString): string => {
  if (column.cosType === 'link' && column.query && column.query.length) {
    const paramsObj: KeyString = {}
    column.query.forEach((item: string) => {
      if (item.indexOf('=') !== -1) {
        const [key, value] = item.split('=')
        if (value.indexOf('$') !== -1) {
          paramsObj[key] = rows[value.replace('$', '')]
        } else {
          paramsObj[key] = value
        }
      } else if (item.indexOf('.') !== -1) {
        const keyList = item.split('.')
        const objKey = camelCase(keyList.join('_'))
        const objVal = keyList.reduce((objRow: any, currentKey: string) => objRow && objRow[currentKey], rows)
        paramsObj[objKey] = objVal
      } else {
        paramsObj[item] = rows[item]
      }
    })
    return `${column.path}?${queryString.stringify(paramsObj)}`
  }
  return ''
}
</script>
<script lang="ts">
export default { name: 'CustomTable' }
</script>

<style lang="scss" scoped>
:deep(.custom-table) {
  .dc-table-set-column-btn {
    background-color: #f3f3f9;
  }
  .ant-table-thead > tr > th {
    border-bottom: none;
    background-color: #f3f3f9;
    &.ant-table-cell-ellipsis {
      overflow: visible;
      white-space: normal;
      word-break: normal;
    }
  }
  .ant-table.ant-table-small .ant-table-tbody > tr > td {
    padding: 8px;
    border-bottom: 1px solid rgba(216, 223, 228, 0.4);
    background-color: #ffffff;
    * {
      text-decoration: none;
    }
  }
  .ant-table-tbody > tr.ant-table-row:hover > td {
    background: #f2f9ff !important;
  }
  .ant-table-cell .ant-btn {
    padding-left: 8px;
    padding-right: 8px;
    &:first-child {
      padding-left: 0;
    }
  }
  .ant-table-cell .ant-dropdown-link {
    padding-left: 9px;
  }
  .ant-table-cell .ant-dropdown-link .iconfont {
    font-size: 14px;
  }
}
</style>
